import { X, User, Phone, Hash, Calendar, CreditCard, FileText, Printer } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { Badge } from '../ui/Badge'

interface SaleDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  sale: any | null // API sale data structure
  formatCurrency: (amount: number) => string
  onReprint?: (saleId: string) => void
}

export const SaleDetailsModal = ({
  isOpen,
  onClose,
  sale,
  formatCurrency,
  onReprint
}: SaleDetailsModalProps) => {
  if (!isOpen || !sale) return null

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  // Calculate discount from API data if available and parse to number
  const orderDiscountAmount = parseFloat(sale.discount_amount || 0)
  const subtotal = parseFloat(sale.subtotal || 0)
  const taxAmount = parseFloat(sale.tax_amount || 0)
  const totalAmount = parseFloat(sale.total_amount || 0)
  const amountReceived = parseFloat(sale.amount_received || 0)
  const changeGiven = parseFloat(sale.change || 0)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
        <CardHeader className="border-b bg-white dark:bg-gray-800 z-10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Sale Details
              </CardTitle>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="secondary">#{sale.bill?.bill_number || sale.bill_number || sale.invoice_number || sale.id.slice(0, 8)}</Badge>
                <Badge variant="outline" className="capitalize">
                  {sale.payment_method}
                </Badge>
                <Badge variant="success">Completed</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onReprint && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onReprint(sale.id)}
                  className="flex items-center gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Reprint
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Sale Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Sale Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Bill Number</span>
                  <span className="font-medium">#{sale.bill?.bill_number || sale.bill_number || sale.invoice_number || sale.id.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Date & Time</span>
                  <span className="font-medium">{formatDate(sale.created_at)}</span>
                </div>
                {(sale.created_by_name || sale.created_by) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Completed By</span>
                    <span className="font-medium">{sale.created_by_name || sale.created_by}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Customer Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {sale.customer ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Name</span>
                      <span className="font-medium">{sale.customer.name}</span>
                    </div>
                    {sale.customer.phone && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          Phone
                        </span>
                        <span className="font-medium">{sale.customer.phone}</span>
                      </div>
                    )}
                    {sale.customer.gst_number && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          GST Number
                        </span>
                        <span className="font-medium">{sale.customer.gst_number}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Walk-in Customer
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Items */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Items ({sale.items?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {(sale.items || []).map((item: any, index: number) => {
                  const unitPrice = parseFloat(item.unit_price || item.price || 0)
                  const totalPrice = parseFloat(item.total_price || item.total_amount || item.total || 0)
                  const quantity = parseFloat(item.quantity || 0)
                  const discountAmount = parseFloat(item.discount_amount || 0)

                  return (
                    <div key={index} className="p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">
                          {item.product_name || item.name}
                        </h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {item.sku && <span>SKU: {item.sku}</span>}
                          <span>
                            {quantity} {item.unit || 'pcs'} × {formatCurrency(unitPrice)}
                          </span>
                          {discountAmount > 0 && (
                            <Badge variant="secondary" size="sm">
                              -{formatCurrency(discountAmount)}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(totalPrice)}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Payment Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Payment Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                <span className="text-gray-900 dark:text-gray-100">{formatCurrency(subtotal)}</span>
              </div>

              {orderDiscountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                  <span>Order Discount</span>
                  <span>-{formatCurrency(orderDiscountAmount)}</span>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Tax</span>
                <span className="text-gray-900 dark:text-gray-100">{formatCurrency(taxAmount)}</span>
              </div>

              <div className="flex justify-between text-lg font-semibold border-t pt-3">
                <span className="text-gray-900 dark:text-gray-100">Total Amount</span>
                <span className="text-gray-900 dark:text-gray-100">{formatCurrency(totalAmount)}</span>
              </div>

              <div className="border-t pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Payment Method</span>
                  <Badge variant="outline" className="capitalize">{sale.payment_method}</Badge>
                </div>

                {sale.payment_method === 'cash' && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Amount Received</span>
                      <span className="text-gray-900 dark:text-gray-100">{formatCurrency(amountReceived || totalAmount)}</span>
                    </div>
                    {(changeGiven > 0 || (amountReceived > 0 && amountReceived > totalAmount)) && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Change Given</span>
                        <span className="text-blue-600 dark:text-blue-400 font-medium">{formatCurrency(changeGiven || (amountReceived - totalAmount))}</span>
                      </div>
                    )}
                  </>
                )}

                {sale.payment_method === 'split' && sale.payment_details && (
                  <div className="space-y-1">
                    {Object.entries(sale.payment_details).map(([method, amount]) => {
                      const amountNum = typeof amount === 'number' ? amount : parseFloat(amount)
                      if (amountNum > 0) {
                        return (
                          <div key={method} className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400 capitalize">{method}</span>
                            <span className="text-gray-900 dark:text-gray-100">{formatCurrency(amountNum)}</span>
                          </div>
                        )
                      }
                      return null
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  )
}